import { Check, Plus, Search } from 'lucide-react';
import { useId, useState, type KeyboardEvent, type ReactNode } from 'react';
import { RANKING_KEYWORDS_MAX, RANKING_KEYWORD_MAX_LENGTH, normaliseKeyword, sameKeyword, type KeywordSuggestionSource } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Input } from '../../../components/Input';
import { Popover, usePopover } from '../../../components/Popover';
import { useModule } from '../../../lib/modules';
import { useKeywordSuggestions } from '../api/ranking-api';
import styles from './ranking.module.css';

// F23.09 and F23.12 (ADR 0049, as in the mockup with Jev switched off): the keywords as on/off chips — the
// ones in use with a tick, the suggested ones with "+" and how many CVs hold each — and a box for your own.
// A keyword narrows the list to CVs that hold it; no score, weight or tab moves (ADR 0048).

const SOURCE_LABELS: Readonly<Record<KeywordSuggestionSource, string>> = {
  criteria: 'From your screening criteria',
  job: 'From the job description',
  cvs: 'Common in these CVs',
};

interface KeywordPanelProps {
  readonly jobId: string;
  readonly keywords: readonly string[];
  readonly onChange: (keywords: readonly string[]) => void;
  /** How many of the job's CVs hold a keyword in use, once looked up. */
  readonly matchCount: (word: string) => number | null;
  /** What the keywords leave in the open tab, or "Looking through the CVs…". */
  readonly note: string | null;
  /** ADR 0050: inside the navy Ask Jev panel as its "Hot filters" row, rather than a card of its own. */
  readonly isInJevPanel?: boolean;
  /** Inside the Keywords button's pop-up, which is the frame. */
  readonly isInPopover?: boolean;
}

/**
 * Owner 2026-11-01 Q9: on ICP match the keywords open from a button in the filter row, so the table is in view. The words in
 * use still show as chips in the filter summary above the table.
 */
export function KeywordsButton(props: Omit<KeywordPanelProps, 'isInJevPanel' | 'isInPopover'>): ReactNode {
  const { anchor, triggerRef, toggle, close } = usePopover();
  const count = props.keywords.length;
  return (
    <>
      <Button ref={triggerRef} variant="outline" icon={Search} aria-expanded={anchor !== null} onClick={toggle}>
        {count > 0 ? `Keywords (${count})` : 'Keywords'}
      </Button>
      <Popover anchor={anchor} onClose={close} label="Keywords in the CV" align="right" width={480}>
        <KeywordPanel {...props} isInPopover />
      </Popover>
    </>
  );
}

export function KeywordPanel({ jobId, keywords, onChange, matchCount, note, isInJevPanel = false, isInPopover = false }: KeywordPanelProps): ReactNode {
  const isCriteriaOn = useModule('screening_criteria');
  const titleId = useId();
  const inputId = useId();
  const suggestions = useKeywordSuggestions(jobId);
  const [own, setOwn] = useState('');
  const isFull = keywords.length >= RANKING_KEYWORDS_MAX;
  const offered = (suggestions.data?.suggestions ?? []).filter((suggestion) => !keywords.some((word) => sameKeyword(word, suggestion.word)));
  const readable = suggestions.data?.readableCvs ?? 0;

  function add(word: string): void {
    const clean = normaliseKeyword(word);
    if (clean === '' || isFull || keywords.some((existing) => sameKeyword(existing, clean))) return;
    onChange([...keywords, clean]);
  }

  function onOwnKey(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    add(own);
    setOwn('');
  }

  const chips = (
    <div className={styles.keywordChips} role="group" aria-label="Keywords" data-tone={isInJevPanel ? 'navy' : undefined}>
      {isInJevPanel && (
        <span id={titleId} className={styles.hotEyebrow}>
          Hot filters
        </span>
      )}
      {keywords.map((word) => {
        const count = matchCount(word);
        return (
          <button key={word} type="button" className={styles.keywordChip} aria-pressed onClick={() => onChange(keywords.filter((existing) => existing !== word))} title={`In use — click to stop narrowing by "${word}"`}>
            <Check size={14} aria-hidden />
            {word}
            {count !== null && <span className={styles.keywordChipCount}>{count.toLocaleString('en-US')}</span>}
          </button>
        );
      })}
      {!isFull &&
        offered.map((suggestion) => (
          <button
            key={suggestion.word}
            type="button"
            className={styles.keywordChip}
            aria-pressed={false}
            onClick={() => add(suggestion.word)}
            title={`${suggestion.source === 'criteria' && !isCriteriaOn ? 'From the ideal candidate' : SOURCE_LABELS[suggestion.source]}${suggestion.isJevPicked ? ', and Jev says this job needs it' : ''} · in ${suggestion.count.toLocaleString('en-US')} of ${readable.toLocaleString('en-US')} readable CVs`}
          >
            <Plus size={14} aria-hidden />
            {suggestion.word}
            <span className={styles.keywordChipCount}>{suggestion.count.toLocaleString('en-US')}</span>
          </button>
        ))}
      <span className={styles.keywordOwn}>
        <label htmlFor={inputId} className={styles.srOnly}>
          Your own keyword
        </label>
        <Input
          id={inputId}
          value={own}
          maxLength={RANKING_KEYWORD_MAX_LENGTH}
          disabled={isFull}
          placeholder={isFull ? `Up to ${RANKING_KEYWORDS_MAX} keywords` : 'Your own keyword, then Enter'}
          onChange={(event) => setOwn(event.target.value)}
          onKeyDown={onOwnKey}
        />
      </span>
    </div>
  );
  if (isInJevPanel) {
    return (
      <>
        {chips}
        {note && (
          <p className={styles.hotNote} aria-live="polite">
            {note}
          </p>
        )}
      </>
    );
  }
  return (
    <section className={styles.keywordPanel} data-in-popover={isInPopover || undefined} aria-labelledby={titleId}>
      <div className={styles.keywordPanelHead}>
        <h2 id={titleId} className={styles.keywordPanelTitle}>
          Keywords in the CV
        </h2>
        <span className={styles.keywordPanelHint}>Each one narrows the list to CVs that hold it. Scores never change.</span>
      </div>
      {chips}
      <p className={styles.keywordPanelNote} aria-live="polite">
        {note ?? `Suggested from ${isCriteriaOn ? 'your screening criteria' : 'the ideal candidate'}, the job description and words common in these CVs.`}
      </p>
    </section>
  );
}
